import type { SourceId } from './sources.js';

/**
 * L'ETAT DU CENTRE MEMORISE PAR PERSONNE ET PAR POSTE (`localStorage`, cle
 * `taki:centre:<orgId>:<uid>`, `cleEtatCentre`) :
 * la largeur choisie de la liste des conversations, la source et la
 * conversation regardees. Les EPINGLES sont au serveur (R12 : personnelles, d'une session
 * a l'autre). La TAILLE de la fenetre est celle du panneau de main
 * (`surfaces/liaPanelSize.ts`, cle `taki:lia:taille`). On retrouve le centre
 * comme on l'a laisse, et cet etat ne vaut que pour ce poste (CONCEPTION,
 * section 1b : « memorise par poste »).
 *
 * Tout est PUR et teste : la lecture tolere une valeur corrompue (etat par
 * defaut), l'ecriture ne jette jamais (stockage indisponible = etat de page).
 */
export interface EtatCentre {
  /**
   * La largeur de la liste des conversations, en px, choisie en glissant sa
   * bordure droite (retouches du 06/10/2026). Elle remplace « liste depliee
   * ou repliee » : fenetre large, la liste est toujours la. Par poste, comme
   * la taille de la fenetre : la geometrie depend de l'ecran, pas de la personne.
   */
  largeurListe: number;
  source: SourceId;
  /** La conversation regardee par source : on revient au meme sujet Lia d'une ouverture a l'autre. */
  conversationParSource: Partial<Record<SourceId, string>>;
}

export const CLE_ETAT_CENTRE = 'taki:centre';

/**
 * La cle d'UNE personne dans UNE org (P9 du 06/10/2026) : deux comptes sur le
 * meme poste ne se rouvrent pas l'un l'autre leur derniere conversation.
 * L'ancienne cle commune (`CLE_ETAT_CENTRE`) n'est plus que lue, en repli.
 */
export function cleEtatCentre(orgId: string, uid: string): string {
  return `${CLE_ETAT_CENTRE}:${orgId}:${uid}`;
}

export const ETAT_CENTRE_DEFAUT: EtatCentre = {
  largeurListe: 240,
  source: 'lia',
  conversationParSource: {},
};

const SOURCES: readonly SourceId[] = ['notifications', 'ici', 'lia', 'canaux', 'directs'];

export function lireEtatCentre(raw: string | null): EtatCentre {
  if (!raw) return ETAT_CENTRE_DEFAUT;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return ETAT_CENTRE_DEFAUT;
    const v = value as Record<string, unknown>;
    const conversationParSource: Partial<Record<SourceId, string>> = {};
    if (v['conversationParSource'] && typeof v['conversationParSource'] === 'object') {
      for (const [source, id] of Object.entries(v['conversationParSource'] as Record<string, unknown>)) {
        if (SOURCES.includes(source as SourceId) && typeof id === 'string' && id.length > 0) conversationParSource[source as SourceId] = id;
      }
    }
    return {
      largeurListe: typeof v['largeurListe'] === 'number' && Number.isFinite(v['largeurListe'])
        ? bornerLargeurListe(v['largeurListe'], Number.POSITIVE_INFINITY) : ETAT_CENTRE_DEFAUT.largeurListe,
      source: SOURCES.includes(v['source'] as SourceId) ? v['source'] as SourceId : 'lia',
      conversationParSource,
    };
  } catch {
    return ETAT_CENTRE_DEFAUT;
  }
}

export function ecrireEtatCentre(etat: EtatCentre, storage: Pick<Storage, 'setItem'> | null, cle = CLE_ETAT_CENTRE): void {
  try { storage?.setItem(cle, JSON.stringify(etat)); } catch { /* Stockage indisponible : l'etat vaut pour cette page. */ }
}

/**
 * ECHAP FERME D'ABORD CE QUI EST OUVERT DANS LA FENETRE (essai du soir,
 * ecart I) : un menu, une liste (mentions), une fenetre de confirmation, un
 * renommage en place. Ce n'est que lorsqu'il n'y a plus rien d'ouvert qu'un
 * Echap ferme la fenetre, comme sur main. Un composant qui consomme Echap le
 * dit par `preventDefault` ; la presence d'une surcouche dans la page suffit
 * aussi (l'ecouteur du menu a pu la fermer juste avant, dans le meme Echap).
 */
export const SURCOUCHES_DE_LA_FENETRE = '[role="menu"], [role="listbox"], [role="dialog"], input[aria-label^="Renommer"]';

export function echapFermeLaFenetre(evenement: Pick<KeyboardEvent, 'key' | 'defaultPrevented'>, page: Pick<Document, 'querySelector'> | null): boolean {
  if (evenement.key !== 'Escape' || evenement.defaultPrevented) return false;
  return page?.querySelector(SURCOUCHES_DE_LA_FENETRE) == null;
}

/** `Ctrl Maj L` (ou `Cmd Maj L` sur Mac) : ouvre la fenetre ; sous 1100 px, ouvre et ferme la liste superposee. */
export function estRaccourciRail(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): boolean {
  return (event.ctrlKey || event.metaKey) && event.shiftKey && !event.altKey && event.key.toLowerCase() === 'l';
}

/**
 * LA FENETRE FLOTTANTE (maquettes hub-d, hub-e1, hub-e2, retour 19 du
 * fondateur) : une carte posee AU-DESSUS de l'app, ancree en bas a droite avec
 * une marge, l'app visible et utilisable derriere. Sa taille est celle du
 * panneau de main (448 x 666 par defaut, poignees, Agrandir) ; la liste depliee remplace le rail d'icones (2,75rem) par une
 * colonne de 240 px, donc la fenetre s'elargit VERS LA GAUCHE de 196 px et la
 * conversation garde sa largeur.
 */
export const FENETRE_MARGE = 24;
export const LARGEUR_RAIL_ICONES = 44;
export const LARGEUR_LISTE_RAIL = 240;
/** Sous cette largeur de fenetre du navigateur, la liste se superpose au fil (hub-e3) au lieu d'elargir la fenetre. */
export const SEUIL_SUPERPOSITION = 1100;

export function listeSeSuperpose(largeurNavigateur: number): boolean {
  return largeurNavigateur < SEUIL_SUPERPOSITION;
}

/** La liste glissee : 180 px au moins, 480 au plus, et la conversation garde 280 px dans la fenetre. */
export const LARGEUR_LISTE_MIN = 180;
export const LARGEUR_LISTE_MAX = 480;
export const LARGEUR_CONVERSATION_MIN = 280;

/** La largeur de la liste, bornee pour une fenetre de `largeurFenetre` px. */
export function bornerLargeurListe(largeur: number, largeurFenetre: number): number {
  const plafond = Math.min(LARGEUR_LISTE_MAX, largeurFenetre - LARGEUR_CONVERSATION_MIN);
  return Math.round(Math.max(LARGEUR_LISTE_MIN, Math.min(plafond, largeur)));
}

/** Le rectangle de la fenetre flottante : la taille du panneau, plus la liste depliee, borne a la fenetre du navigateur moins ses marges. */
export function geometrieFenetre(taille: { width: number; height: number }, railDeplie: boolean, navigateur: { width: number; height: number }, superpose: boolean): { width: number; height: number } {
  const largeur = taille.width + (railDeplie && !superpose ? LARGEUR_LISTE_RAIL - LARGEUR_RAIL_ICONES : 0);
  return {
    width: Math.min(largeur, Math.max(320, navigateur.width - 2 * FENETRE_MARGE)),
    height: Math.min(taille.height, Math.max(360, navigateur.height - 2 * FENETRE_MARGE)),
  };
}
